/**
 * database.ts — Gombita Food Offline-First Local Database
 * Uses Dexie.js (IndexedDB wrapper) to persist data and sync queue locally.
 * 
 * Schema:
 *   syncQueue  — Pending API operations to replay when back online
 */

import Dexie, { type Table } from 'dexie';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type SyncStatus = 'pending' | 'processing' | 'synced' | 'failed';

export interface SyncQueueItem {
  /** Auto-incremented primary key */
  id?: number;
  /** Relative API endpoint e.g. "/orders" */
  url: string;
  /** HTTP method */
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** Serialized JSON body */
  body?: string;
  /** ISO timestamp when enqueued */
  createdAt: string;
  /** Number of failed sync attempts */
  attempts: number;
  /** Current status */
  status: SyncStatus;
  /** Human-readable description for debugging */
  description?: string;
  /** Optional: optimistic local id so UI can match result */
  tempId?: string;
  /** Branch ID at the time of the operation */
  branchId?: string | null;
}

// ---------------------------------------------------------------------------
// Dexie Database class
// ---------------------------------------------------------------------------

class GombitaDB extends Dexie {
  syncQueue!: Table<SyncQueueItem>;

  constructor() {
    super('GombitaFoodDB');

    this.version(1).stores({
      // &id = auto-increment primary key
      // status = indexed for fast queries
      // createdAt = indexed for FIFO ordering
      syncQueue: '++id, status, createdAt',
    });
  }
}

// ---------------------------------------------------------------------------
// Singleton
// ---------------------------------------------------------------------------

export const db = new GombitaDB();

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Add an operation to the sync queue */
export async function enqueueSync(item: Omit<SyncQueueItem, 'id' | 'createdAt' | 'attempts' | 'status'>): Promise<number> {
  return db.syncQueue.add({
    ...item,
    createdAt: new Date().toISOString(),
    attempts: 0,
    status: 'pending',
  });
}

/** Get all pending items ordered by creation time (FIFO) */
export async function getPendingItems(): Promise<SyncQueueItem[]> {
  return db.syncQueue
    .where('status')
    .equals('pending')
    .sortBy('createdAt');
}

/** Mark an item as synced and remove it from the queue */
export async function markSynced(id: number): Promise<void> {
  await db.syncQueue.delete(id);
}

/** Mark an item as failed (increment attempts) */
export async function markFailed(id: number): Promise<void> {
  const item = await db.syncQueue.get(id);
  if (!item) return;

  const attempts = (item.attempts || 0) + 1;
  await db.syncQueue.update(id, {
    status: attempts >= 5 ? 'failed' : 'pending',
    attempts,
  });
}

/** Count pending items */
export async function pendingCount(): Promise<number> {
  return db.syncQueue.where('status').equals('pending').count();
}

/** Clear all successfully synced or permanently failed items */
export async function clearProcessed(): Promise<void> {
  await db.syncQueue.where('status').anyOf(['synced', 'failed']).delete();
}
